import React from "react";
import { Box, Chip, Typography, useTheme, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * Component to display connected accounts information for an integration
 * @param {Array} connectedAccounts - Array of connected account objects
 * @param {String} integrationKey - The integration key to match accounts
 */
const ConnectedAccountsChip = ({ connectedAccounts, integrationKey }) => {
  const theme = useTheme();

  const accounts = connectedAccounts || [];

  if (accounts.length === 0) {
    return null;
  }

  // Separate active and inactive accounts
  const activeAccounts = accounts.filter(
    (acc) => acc.is_active === true || acc.is_active === "true"
  );
  const inactiveAccounts = accounts.filter(
    (acc) =>
      acc.is_active === false || acc.is_active === "false" || !acc.is_active
  );

  // Check if we have account labels
  const hasActiveLabels = activeAccounts.some(
    (acc) => acc.account_label && acc.account_label.trim() !== ""
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 1.5,
        backgroundColor: theme.customColors.background.tableRow,
        borderRadius: "8px",
        border: `1px dashed ${theme.customColors.border.dashed}`,
      }}
    >
      {/* Active Accounts Line */}
      {activeAccounts.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* Header with count */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              backgroundColor: "white",
              borderRadius: "6px",
              border: `1px solid ${theme.customColors.border.subtle}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.customColors.text.strong,
                fontWeight: 600,
                fontSize: "12px",
                letterSpacing: "0.02em",
              }}
            >
              {activeAccounts.length} Active Account
              {activeAccounts.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          {/* Active Account Chips */}
          {hasActiveLabels &&
            activeAccounts
              .filter(
                (acc) => acc.account_label && acc.account_label.trim() !== ""
              )
              .map((account, index) => (
                <Chip
                  key={`${account.integration_key}-active-${index}`}
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label={account.account_label}
                  size="small"
                  sx={{
                    height: "28px",
                    backgroundColor: "white",
                    color: theme.customColors.text.strong,
                    fontWeight: 500,
                    fontSize: "12px",
                    border: `1px solid ${theme.customColors.border.main}`,
                    borderRadius: "6px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    "& .MuiChip-icon": {
                      color: theme.customColors.status.success.main,
                      marginLeft: "6px",
                      fontSize: "14px",
                    },
                  }}
                />
              ))}
        </Box>
      )}

      {/* Inactive Accounts Line */}
      {inactiveAccounts.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* Header with count */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              backgroundColor: "white",
              borderRadius: "6px",
              border: `1px solid ${theme.customColors.status.error.border}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.customColors.status.error.text,
                fontWeight: 600,
                fontSize: "12px",
                letterSpacing: "0.02em",
              }}
            >
              {inactiveAccounts.length} Inactive Account
              {inactiveAccounts.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          {/* Inactive Account Chips */}
          {inactiveAccounts.map((account, index) => {
            const hasLabel =
              account.account_label && account.account_label.trim() !== "";
            const label = hasLabel
              ? account.account_label
              : `Account ${index + 1}`;
            const tooltipText =
              account.inactivity_reason || "Account became inactive";

            return (
              <Tooltip
                key={`${account.integration_key}-inactive-${index}`}
                title={tooltipText}
                arrow
              >
                <Chip
                  icon={<ErrorOutlineIcon fontSize="small" />}
                  label={label}
                  size="small"
                  sx={{
                    height: "28px",
                    backgroundColor: "white",
                    color: theme.customColors.status.error.text,
                    fontWeight: 500,
                    fontSize: "12px",
                    border: `1px solid ${theme.customColors.status.error.border}`,
                    borderRadius: "6px",
                    boxShadow: "0 1px 2px rgba(239, 68, 68, 0.1)",
                    "& .MuiChip-icon": {
                      color: theme.customColors.status.error.main,
                      marginLeft: "6px",
                      fontSize: "14px",
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ConnectedAccountsChip;
